import React from 'react';
import { Box, TextField, Radio, RadioGroup, FormControlLabel, FormControl, Checkbox, FormGroup } from '@mui/material';
import LikertSlider from './LikertSlider';
import NumericSlider from './NumericSlider';

function normalizeOptions(options) {
  if (!Array.isArray(options)) return [];
  return options.map(o => (typeof o === 'string' ? o : (o?.value ?? String(o))));
}

function gridDimensions(n) {
  if (n <= 1) return { rows: 1, cols: n };
  let best = { rows: 1, cols: n };
  let minDiff = Infinity;
  for (let r = 1; r <= Math.sqrt(n); r++) {
    if (n % r === 0) {
      const c = n / r;
      const diff = Math.abs(c - r);
      if (diff < minDiff) {
        minDiff = diff;
        best = { rows: r, cols: c };
      }
    }
  }
  // place the larger dimension as columns per spec
  const rows = Math.min(best.rows, best.cols);
  const cols = Math.max(best.rows, best.cols);
  return { rows, cols };
}

export default function QuestionItem({ question, value, onChange }) {
  const q = question || {};
  const opts = normalizeOptions(q.options);

  switch (q.type) {
    case 'text':
    case 'textarea': {
      const v = typeof value === 'string' ? value : (typeof q.defaultValue === 'string' ? q.defaultValue : '');
      return (
        <TextField
          fullWidth
          multiline
          value={v}
          onChange={(e) => onChange?.(e.target.value)}
          size="small"
        />
      );
    }
    case 'boolean': {
      const options = opts.length === 2 ? opts : ['Yes', 'No'];
      return (
        <FormControl>
          <RadioGroup row value={value ?? ''} onChange={(e) => onChange?.(e.target.value)}>
            {options.map((opt, i) => (
              <FormControlLabel key={i} value={opt} control={<Radio />} label={opt} />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }
    case 'single_choice':
    case 'radio': {
      return (
        <FormControl>
          <RadioGroup value={value ?? ''} onChange={(e) => onChange?.(e.target.value)}>
            {opts.map((opt, i) => (
              <FormControlLabel key={i} value={opt} control={<Radio />} label={opt} />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }
    case 'multiple_selection':
    case 'checkbox': {
      // Support legacy and new config shapes
  const ms = q.multipleSelection || q.selectionConfig || {};
  const layout = ms.layout || q.layout || 'vertical'; // 'vertical' | 'horizontal' | 'grid'
  const max = Number.isFinite(ms.max) ? ms.max : null;
      const current = Array.isArray(value) ? value : (Array.isArray(q.defaultValue) ? q.defaultValue : []);

      const handleToggle = (checked, opt) => {
        const next = new Set(current);
        if (checked) {
          // Enforce max if provided
          if (max !== null && current.length >= max && !current.includes(opt)) {
            return; // ignore further additions
          }
          next.add(opt);
        } else {
          next.delete(opt);
        }
        onChange?.(Array.from(next));
      };

      const atMax = max !== null && current.length >= max;

      if (layout === 'grid') {
        const { cols } = gridDimensions(opts.length || 1);
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 1 }}>
            {opts.map((opt, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    checked={current.includes(opt)}
                    onChange={(e) => handleToggle(e.target.checked, opt)}
                    disabled={!current.includes(opt) && atMax}
                  />
                }
                label={opt}
              />
            ))}
          </Box>
        );
      }
      return (
        <FormGroup row={layout === 'horizontal'}>
          {opts.map((opt, i) => (
            <FormControlLabel
              key={i}
              control={
                <Checkbox
                  checked={current.includes(opt)}
                  onChange={(e) => handleToggle(e.target.checked, opt)}
                  disabled={!current.includes(opt) && atMax}
                />
              }
              label={opt}
            />
          ))}
        </FormGroup>
      );
    }
    case 'slider':
      return (
        <NumericSlider question={q} value={value} onChange={onChange} />
      );
    case 'likert':
      return (
        <LikertSlider question={q} value={value} onChange={onChange} />
      );
    default:
      return (
        <TextField
          fullWidth
          multiline
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange?.(e.target.value)}
          size="small"
        />
      );
  }
}
